import { useEffect } from "react";

import { MenuItem, Select, FormControl, InputLabel, FormHelperText } from "@mui/material"
import { useAccessTariffs } from "../../hooks/data/useAccessTariffs";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StepBaseContractFormData } from "../../types/stepsFormData";

type AccessTariffsSelectProps = ({
  gas: false;
  powers: number[];
} | {
  gas: true,
  powers?: never;
}) & {
  label: string;
  readOnly?: boolean;
};

const AccessTariffSelectField = ({
  label, 
  readOnly = false,
  gas = false,
  powers
}: AccessTariffsSelectProps) => {

  const { t } = useTranslation();

  const { 
    control,
    setValue,
    getValues
  // TODO If needed make this useFormContext recieve a StepBaseContractFormData
  // generic value passed from the constructor
  } = useFormContext<Pick<StepBaseContractFormData, "accessTariff">>();

  const accessTariffsData = useAccessTariffs({
    power: Math.max(...(powers ?? [0])),
    gas
  });

  const setTariffValueFromId = (id: number) => {
    if ( accessTariffsData.isSuccess && accessTariffsData.data) {
      const tariff = accessTariffsData.data.find(tariff => tariff.id === id);
      if ( tariff ) {
        setValue("accessTariff", tariff);
      } else {
        setValue("accessTariff", {id: -1, name: ""});
      }
    }
  }

  useEffect(() => {
    if (!accessTariffsData.isFetching) {
      if (accessTariffsData.isSuccess) {
        if (accessTariffsData.data.length === 1) {
          setTariffValueFromId(accessTariffsData.data[0].id);
        } else if (
          accessTariffsData.data.length === 0 ||

          // Can't find the current selected id in the ones fetched
          !accessTariffsData.data.some(
            tariff => tariff.id === getValues("accessTariff.id"))
        ) {
          setValue("accessTariff", {id: -1, name: ""});
        }
      } else {
        setValue("accessTariff", {id: -1, name: ""});
      }
    }
  }, [
    accessTariffsData.data, 
    accessTariffsData.isSuccess, 
    accessTariffsData.isFetching
  ]);

  return (
    <Controller
      name={"accessTariff.id"}
      control={control}
      rules={{ required: true }}
      render={({ field, fieldState }) => <>
        <FormControl
          disabled={readOnly}
          style={{width: "100%"}}
        >
          <InputLabel shrink htmlFor="" id="access-tariff-label">
            {(label || t('common:text.contractation_access_tariff')) + "*"}
          </InputLabel>
          <Select
            autoWidth={true}
            error={!!fieldState.error}
            labelId="access-tariff-label"
            disabled={
              accessTariffsData.isSuccess && accessTariffsData.data.length === 0 || 
              accessTariffsData.isFetching || readOnly
            }
            {...field}
            value={field.value > 0 ? 
              field.value : ''} 
            onChange={(e) => setTariffValueFromId(Number(e.target.value))}
          >
            {(accessTariffsData.isSuccess && accessTariffsData.data.length &&
              accessTariffsData.data.map((c) => {
                return <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>;
              })) ||
              []}
          </Select>
        </FormControl>
        {fieldState.error?.message &&
          <FormHelperText error={true}>
            {t(fieldState.error.message)}
          </FormHelperText>
        }
      </>}
    />
  );
};

export default AccessTariffSelectField;
